import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { BodyHighlight } from "helpers/TypographyComponents";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDocument } from "store/documentStore";

interface IRejectDocumentModal {
    document: IDocument | undefined;
    onClose: () => void;
    onReject: (reason: string) => Promise<void>;
}

/** Rejects a document uploaded by the author, with a reason sent to them. */
const RejectDocumentModal = ({ document, onClose, onReject }: IRejectDocumentModal) => {
    const [t] = useTranslation();
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const handleReject = async () => {
        setSaving(true);
        try {
            await onReject(reason.trim());
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalBase
            open={!!document}
            onClose={onClose}
            modalTitle={t("BeneficiaryDocuments.Documents.RejectTitle")}
            modalSubtitle={t("BeneficiaryDocuments.Documents.RejectSubtitle", { name: document?.name ?? "" })}
        >
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <BodyHighlight tag="label" htmlFor="reject-reason" color="black">
                        {t("BeneficiaryDocuments.Documents.RejectReasonLabel")}
                        <BodyHighlight tag="span" color="danger">
                            {" *"}
                        </BodyHighlight>
                    </BodyHighlight>
                    <StyledTextField
                        id="reject-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t("BeneficiaryDocuments.Documents.RejectReasonPlaceholder")}
                        multiline
                        minRows={4}
                        fullWidth
                        autoFocus
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <StyledButton buttonType="outlined" onClick={onClose}>
                        {t("BeneficiaryDocuments.Form.Cancel")}
                    </StyledButton>
                    <StyledButton
                        buttonType="primary"
                        onClick={handleReject}
                        loading={saving}
                        disabled={!reason.trim()}
                    >
                        {t("BeneficiaryDocuments.Documents.Reject")}
                    </StyledButton>
                </div>
            </div>
        </ModalBase>
    );
};

export default RejectDocumentModal;
